import { useRef, useState } from 'react';

import { Button } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Modal, ModalProps, RadioChangeEvent, Typography } from 'antd';

import { CategoryTreeItem } from '~apis/categories';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import {
  useCategoriesTree,
  useDeleteCompanySpecificCategories,
} from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import CategoryMigrationList from './CategoryMigrationList';
import CategoryTreeSelect from './CategoryTreeSelect';
import { StepFooter } from './common/component/RemoveModal/Footer';
import RemoveTypeSelectStepContnet from './common/component/RemoveModal/RemoveTypeSelectStepContent';

interface Props extends ModalProps {
  item: FlattenTreeResult<CategoryTreeItem>;
  defaultStep?: number;
}

const CategoryLabelRemoveModal: React.FC<Props> = ({ item, defaultStep, ...props }) => {
  const { companyId } = useDemoAwareCompanyId();
  const { data } = useCategoriesTree({ companyId });
  const [changeParentCategory, setChangeParentCategory] =
    useState<FlattenTreeResult<CategoryTreeItem> | null>(null);
  const [removalType, setRemovalType] = useState('remove');
  const [step, setStep] = useState(defaultStep ?? 1);
  const [changeMap, setChangeMap] = useState<Record<string, string>>({});
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { mutateAsync: deleteCategories, isLoading } = useDeleteCompanySpecificCategories();
  const label = '계정';
  const categories = data?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categories ?? [])
    .filter((item) => !!item.value?.name)
    .filter(
      (treeItem) =>
        treeItem?.value?.cashFlowType === item?.value?.cashFlowType &&
        !treeItem.value?.contraCategoryId,
    );

  const onChange = (e: RadioChangeEvent) => {
    setRemovalType(e.target.value);
    if (e.target.value === 'remove' && changeParentCategory) {
      setChangeParentCategory(null);
    }
  };

  const onRemove = async () => {
    // changeMap에 있는 자식들은 migration
    // 본인은 삭제 or migration

    if (!item?.value?.id || !item?.value?.categoryLabelKey) return;
    const categoryId = item.value.categoryLabelKey.split('_')[0];

    const response = await deleteCategories({
      companyId,
      id: item.value.id,
      categoryId: Number(categoryId),
      deleteRequest: {
        moveChildrenTo: changeParentCategory?.key,
        migrations: Object.entries(changeMap).map(([from, to]) => {
          const [categoryIdForFrom, companyDetailedCategoryIdForFrom] = from.split('_');
          const [categoryIdForTo, companyDetailedCategoryIdForTo] = to?.split('_') ?? [null, null];
          return {
            from: {
              categoryId: Number(categoryIdForFrom),
              companyDetailedCategoryId: Number(companyDetailedCategoryIdForFrom),
            },
            to: {
              categoryId: Number(categoryIdForTo),
              companyDetailedCategoryId: Number(companyDetailedCategoryIdForTo),
            },
          };
        }),
      },
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('삭제되었습니다.');
      mixpanel.track(MIXPANEL_EVENT.CATEGORY.DELETE_DETAILED_CATEGORY, {
        type: changeParentCategory?.key ? 'CHANGE_PARENT' : 'DIRECT_DELETE',
      });
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: 'CATEGORY_DELETE',
      });
      //@ts-ignore
      props.onCancel();
    } else {
      Toast.error('삭제에 실패했습니다.');
    }
  };

  const getStepContent = () => {
    const childrenTree = flattenTree(item.children).filter((item) => item.children.length > 0);

    switch (step) {
      case 1:
        return (
          <RemoveTypeSelectStepContnet
            label={label}
            item={item}
            childrenTree={childrenTree}
            onChange={onChange}
            removalType={removalType}
            treeSelectSection={
              <CategoryTreeSelect
                disableActionOnInactiveItem={false}
                closeOnSelect
                treeRef={wrapperRef}
                footerElement={<div />}
                onSelect={(_, key) => {
                  const target = flattenedTree.find((node) => key.includes(node.key));
                  if (!target) return;
                  setChangeParentCategory(target);
                }}
                triggerElement={
                  <Button
                    border
                    disable={removalType !== 'change-parent'}
                    style={{
                      width: 'fit-content',
                      marginLeft: 18,
                    }}
                  >
                    <Typography.Text
                      style={{
                        color:
                          removalType !== 'change-parent' ? Colors.grayOpacity22 : Colors.gray700,
                        ...typographyStyle['14_400'],
                      }}
                    >
                      {changeParentCategory
                        ? flattenedTree.find(
                            (item) =>
                              item.value?.categoryLabelKey ===
                                changeParentCategory?.value?.categoryLabelKey &&
                              item?.value?.type === changeParentCategory?.value?.type,
                          )?.value?.name
                        : '계정 선택'}
                    </Typography.Text>
                  </Button>
                }
                treeList={flattenedTree.filter((treeItem) => {
                  return (
                    !childrenTree.map((c) => c.key).includes(treeItem.key) &&
                    treeItem.key !== item.key
                  );
                })}
              />
            }
          />
        );
      case 2:
        const depth = item.namePath.split('.').length;

        const filteredChildrenTree = [item, ...childrenTree];
        const targetItems =
          removalType === 'change-parent' || defaultStep === 2 ? [item] : filteredChildrenTree;

        return (
          <CategoryMigrationList
            changeMap={changeMap}
            setChangeMap={setChangeMap}
            targetItems={targetItems}
            depth={depth}
            parentItem={item}
          />
        );
    }
  };

  return (
    <Modal
      title={
        <Typography.Text
          style={{
            ...typographyStyle['16_600'],
            color: Colors.gray800,
          }}
        >
          {`${item.value?.name} ${label} 삭제`}
        </Typography.Text>
      }
      footer={
        <StepFooter
          step={step}
          setChangeMap={setChangeMap}
          setStep={setStep}
          onRemove={onRemove}
          // @ts-ignore
          onCancel={() => props.onCancel()}
          isLoading={isLoading}
        />
      }
      {...props}
    >
      {getStepContent()}
    </Modal>
  );
};

export default CategoryLabelRemoveModal;
